import { Text } from '@components/v2/ui';
import { IconProjectLocation } from '@components/v2/ui/IconProjectLocation/IconProjectLocation';
import { Title } from '@components/v2/ui/Title';
import { NOT_APPLICABLE } from '@config/constants/common';
import { useValidImage } from '@hooks/useValidImage';
import {
	Avatar,
	Box,
	Flex,
	Group,
	NumberFormatter,
	Stack,
	Tooltip,
} from '@mantine/core';
import useStore from '@store/index';
import { IconQuestionMark } from '@tabler/icons-react';
import { truncateString } from '@utils/functions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './SelectedFinancedProjectsSection.module.css';

const SelectedFinancedProjectsSection = () => {
	const { t } = useTranslation();

	const { offsetAdditionalProjectsSelected } = useStore();

	const totalAdditionality = useMemo(() => {
		return offsetAdditionalProjectsSelected.reduce(
			(sum, p) => sum + (p.price ?? 0),
			0
		);
	}, [offsetAdditionalProjectsSelected]);

	return (
		<Box className={classes.container}>
			<Flex
				justify={'space-between'}
				align={'center'}
				className={classes.containerHeader}
			>
				<Title order={6} c={'#1D3965'}>
					{t(
						'global.projectsFundedThanksToYourContribution'
					)}{' '}
					({offsetAdditionalProjectsSelected?.length ?? 0}
					)
				</Title>

				<Group gap={'10px'}>
					<Text variant="button" c={'#505058'}>
						{t('offsets.totalAdditionality')}
					</Text>
					<Text variant="subtitle1" c={'#617EEF'}>
						<NumberFormatter
							thousandSeparator=" "
							decimalSeparator=","
							value={totalAdditionality}
							decimalScale={2}
							suffix=" €"
						/>
					</Text>
				</Group>
			</Flex>

			<Stack p={'12px'} w={'100%'}>
				<Flex align={'center'} flex={1}>
					<Flex flex={2} align={'center'}>
						<Text variant="caption" c={'#9BA6B7'}>
							{t('global.projects')}
						</Text>
					</Flex>

					<Flex justify={'end'} align={'center'} flex={1}>
						<Text variant="caption" c={'#9BA6B7'}>
							{t('global.totalPerProject')}
						</Text>
					</Flex>
				</Flex>

				{offsetAdditionalProjectsSelected.map(
					(project) => {
						const coverUrl = useValidImage(
							project?.image
						);
						return (
							<Flex align={'center'} flex={1}>
								<Flex
									flex={2}
									gap={'20px'}
									align={'center'}
								>
									<img
										style={{
											borderRadius: '12px',
											objectFit: 'cover',
										}}
										src={coverUrl}
										height="64px"
										width="64px"
									/>
									<Stack gap={0}>
										<Text
											variant="subtitle1"
											c={'#505058'}
										>
											{project.name}
										</Text>
										<Flex
											gap={'var(--mantine-spacing-xs)'}
											mih={'20px'}
										>
											<Flex mih={'20px'}>
												<Group
													justify="center"
													gap={'5px'}
												>
													<Avatar
														radius={'xl'}
														src={`https://flagcdn.com/w40/${project?.country?.code.toLowerCase()}.png`}
														size={18}
													>
														<IconQuestionMark size={18} />
													</Avatar>
													<Tooltip
														label={project?.country?.name}
														disabled={
															project?.country?.name
																.length <= 11
														}
													>
														<Box>
															<Text
																variant={'caption'}
																c={
																	'var(--mantine-color-igluSecondary-9)'
																}
															>
																{truncateString(
																	project?.country?.name,
																	12
																)}
															</Text>
														</Box>
													</Tooltip>
												</Group>
											</Flex>
											{NOT_APPLICABLE && (
												<Flex
													style={{
														minHeight: '20px',
													}}
												>
													<Group gap={'5px'}>
														<IconProjectLocation
															color={'#617EEF'}
														/>

														<Tooltip
															label={project?.region}
															disabled={
																project?.region.length <=
																11
															}
														>
															<Box>
																<Text
																	variant={'caption'}
																	c={
																		'var(--mantine-color-igluSecondary-9)'
																	}
																>
																	{truncateString(
																		project?.region,
																		12
																	)}
																</Text>
															</Box>
														</Tooltip>
													</Group>
												</Flex>
											)}
										</Flex>
									</Stack>
								</Flex>

								<Flex
									justify={'end'}
									align={'center'}
									flex={1}
								>
									<Text variant="subtitle1" c={'#505058'}>
										<NumberFormatter
											thousandSeparator=" "
											decimalSeparator=","
											value={project.price}
											decimalScale={2}
											suffix="€"
										/>
									</Text>
								</Flex>
							</Flex>
						);
					}
				)}
			</Stack>
		</Box>
	);
};

export default SelectedFinancedProjectsSection;
